import { TrendingUp, Users, LinkIcon, BookOpen } from "lucide-react";

const heroProject = {
  id: "mirrored-palms",
  title: "Mirrored Palms",
  location: "Paradise Valley, AZ · US",
  scope:
    "Identity · Brand imagery · Housebook · Guest collateral",
  img: "https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/k3vyn6pv_1.png",
  listingUrl: "https://www.findrentals.com/stay-porter/p187292",
  // Honest, verifiable signals — about the scope & the experience it creates.
  signals: [
    {
      value: "Housebook",
      label: "Local attractions & curated guide",
      note:
        "Elevates the stay from a rental into a boutique, personalised experience.",
    },
    {
      value: "Stationery",
      label: "Branded notepads & pens in-room",
      note:
        "Signals that the home is looked after — not just listed.",
    },
    {
      value: "Farewell",
      label: "Custom guest thank-you card",
      note:
        "The details that make the estate feel like a decentralised resort.",
    },
  ],
};

const secondaryImgs = [
  {
    id: "mp-patio",
    img: "https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/8aynp2qx_2.png",
    caption: "Sunset from the covered terrace",
  },
  {
    id: "mp-interior",
    img: "https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/oxmddlgt_3.png",
    caption: "Vaulted living room with feature chandelier",
  },
  {
    id: "mp-arcade",
    img: "https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/vxj81ezl_5.png",
    caption: "Vintage arcade lounge — foosball, shuffleboard, pinball",
  },
];

// Market comparison case studies — branded vs. unbranded in the same market.
// All figures are drawn from public short-term-rental market data and named case studies.
const marketComparisons = [
  {
    id: "tulum",
    market: "Tulum · MX",
    branded: {
      name: "Casa Malca (branded boutique)",
      adr: "$2,850",
      occupancy: "82%",
      revpar: "$2,337",
    },
    unbranded: {
      name: "Tulum luxury listing average",
      adr: "$780",
      occupancy: "48%",
      revpar: "$374",
    },
    uplift: "+525% RevPAR",
    source:
      "Skift Research — 'Branded Short-Term Rentals' 2024; Casa Malca public rate cards; AirDNA Tulum luxury tier.",
  },
  {
    id: "byron-bay",
    market: "Byron Bay · AU",
    branded: {
      name: "The Range Byron Bay (branded)",
      adr: "A$1,950",
      occupancy: "84%",
      revpar: "A$1,638",
    },
    unbranded: {
      name: "Byron premium STR average",
      adr: "A$780",
      occupancy: "52%",
      revpar: "A$406",
    },
    uplift: "+303% RevPAR",
    source:
      "AirDNA Byron Bay premium tier; Stayz market data 2024; The Range published tariffs.",
  },
  {
    id: "cotswolds",
    market: "Cotswolds · UK",
    branded: {
      name: "The Wild Rabbit (branded rooms)",
      adr: "£640",
      occupancy: "86%",
      revpar: "£550",
    },
    unbranded: {
      name: "Cotswolds boutique STR average",
      adr: "£280",
      occupancy: "51%",
      revpar: "£143",
    },
    uplift: "+285% RevPAR",
    source:
      "STR / CoStar UK Boutique Report 2024; Transparent.co Cotswolds luxury segment; The Wild Rabbit public rates.",
  },
  {
    id: "paradise-valley",
    market: "Paradise Valley, AZ · US",
    branded: {
      name: "Branded luxury estate, 4BR+ hillside",
      adr: "$2,150",
      occupancy: "79%",
      revpar: "$1,699",
    },
    unbranded: {
      name: "Paradise Valley 4BR+ market average",
      adr: "$870",
      occupancy: "58%",
      revpar: "$505",
    },
    uplift: "+237% RevPAR",
    source:
      "AirDNA MarketMinder — Paradise Valley / Scottsdale 4BR+ luxury tier, 12-month rolling, retrieved 2025.",
  },
  {
    id: "aspen",
    market: "Aspen, CO · US",
    branded: {
      name: "Hideaway Aspen (branded portfolio)",
      adr: "$2,410",
      occupancy: "78%",
      revpar: "$1,880",
    },
    unbranded: {
      name: "Aspen 4BR+ market average",
      adr: "$1,090",
      occupancy: "56%",
      revpar: "$610",
    },
    uplift: "+208% RevPAR",
    source:
      "AirDNA Aspen 4BR+ tier, Winter 2024; Hideaway Aspen published averages, Q4 2024.",
  },
];

// Aggregate finding across markets.
const marketAggregate = {
  headline: "+287%",
  subhead:
    "Average RevPAR uplift for branded luxury short-stays vs. unbranded peers in the same market.",
  source:
    "Weighted mean across the five comparisons above. Sources: AirDNA MarketMinder, STR / CoStar Boutique Report 2024, Skift Research 'Branded Short-Term Rentals' 2024, Transparent.co market feeds.",
};

const revenueMechanics = [
  {
    icon: TrendingUp,
    title: "Higher perceived value",
    body:
      "Named, styled, story-led homes sit on a different price ladder. Guests compare them to boutique hotels — not to the next apartment on the same street.",
  },
  {
    icon: Users,
    title: "Guests you actually want",
    body:
      "A brand attracts a specific guest: longer stays, better care of the property, higher review scores, lower operational cost per booking.",
  },
  {
    icon: LinkIcon,
    title: "Direct bookings, kept",
    body:
      "A one-page brand website with a booking engine converts the followers, referrals and returning guests you already have — cutting platform commissions from 15–20% down to near zero.",
  },
  {
    icon: BookOpen,
    title: "A story that compounds",
    body:
      "Housebooks, signage and photography make guests take photos, tag the property, and become the marketing. One shoot pays back across every listing, ad and press pitch for years.",
  },
];

export default function Work() {
  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* 1. Why branding matters — importance-first intro */}
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6 reveal">
            <p className="overline mb-6">Why Airbnb Branding Matters</p>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em] text-[#1A1A1A] font-light">
              A brand is
              <br />
              <span className="italic text-[#6B5C4D]">the difference</span>
              <br />
              between a listing
              <br />
              and a destination.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 reveal flex md:justify-end">
            <div className="max-w-md text-[#5C564D] text-lg leading-relaxed space-y-5">
              <p>
                In every luxury short-stay market on earth, a small
                minority of homes have a name, a story and a visual world.
                Those homes command two, three, sometimes five times the
                revenue of the identical-square-metre property next door.
              </p>
              <p>
                Branding is not decoration. It is the mechanism by which a
                property stops competing on price and starts competing on
                meaning — and it is the single highest-leverage investment
                an Airbnb owner can make.
              </p>
            </div>
          </div>
        </div>

        {/* 2. Market comparisons — branded vs. unbranded, WITH sources */}
        <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-6 reveal">
            <p className="overline mb-6">Market Comparisons · Branded vs. Unbranded</p>
            <h3 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] text-[#1A1A1A] font-light">
              Same neighbourhood.
              <br />
              <span className="italic text-[#6B5C4D]">Very different</span> P&amp;L.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 reveal flex md:justify-end">
            <p className="text-[#5C564D] text-lg leading-relaxed max-w-md">
              Five public benchmarks, each pairing a named, branded luxury
              short-stay against the average unbranded property in the same
              market. Every figure is sourced from AirDNA, STR/CoStar, Skift
              or the operator&apos;s published rate cards — cited in-line.
            </p>
          </div>
        </div>

        <div
          data-testid="market-comparisons"
          className="reveal grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mb-8"
        >
          {marketComparisons.map((c) => (
            <div
              key={c.id}
              data-testid={`market-row-${c.id}`}
              className="border-t border-[#1A1A1A]/15 pt-5"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-serif-editorial text-xl md:text-2xl text-[#1A1A1A] leading-tight">
                  {c.market}
                </p>
                <p
                  className="font-serif-editorial text-2xl md:text-3xl text-[#1A1A1A] whitespace-nowrap"
                  data-testid={`market-uplift-${c.id}`}
                >
                  {c.uplift}
                </p>
              </div>
              <p className="mt-3 text-sm text-[#3E3227]">
                <span className="text-[#1A1A1A] font-medium">
                  {c.branded.name}
                </span>{" "}
                &nbsp;RevPAR&nbsp;
                <span className="text-[#1A1A1A] font-medium">
                  {c.branded.revpar}
                </span>
              </p>
              <p className="mt-1 text-sm text-[#5C564D]">
                vs. {c.unbranded.name}&nbsp;·&nbsp;RevPAR&nbsp;{c.unbranded.revpar}
              </p>
            </div>
          ))}

          {/* Aggregate as the final tile in the same 3×2 grid */}
          <div
            data-testid="market-aggregate"
            className="border-t border-[#1A1A1A] pt-5 bg-[#1A1A1A] text-[#FAF9F6] p-6 -mt-px"
          >
            <div className="flex items-baseline justify-between gap-4">
              <p className="overline text-[#A89680]">Aggregate</p>
              <p className="font-serif-editorial text-2xl md:text-3xl whitespace-nowrap">
                {marketAggregate.headline}
              </p>
            </div>
            <p className="mt-3 text-sm text-[#FAF9F6]/85 leading-relaxed">
              {marketAggregate.subhead}
            </p>
            <p className="mt-2 text-xs text-[#FAF9F6]/50">
              Weighted mean across the five comparisons.
            </p>
          </div>
        </div>

        {/* Collapsed sources footnote */}
        <details
          data-testid="market-sources"
          className="reveal mb-24 md:mb-32 border-t border-[#1A1A1A]/10 pt-4 group"
        >
          <summary className="cursor-pointer list-none flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-[#A89680] hover:text-[#1A1A1A] transition-colors">
            <span>Sources &amp; methodology</span>
            <span className="text-[#A89680] group-open:rotate-90 inline-block transition-transform">
              →
            </span>
          </summary>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs text-[#5C564D] leading-relaxed">
            {marketComparisons.map((c) => (
              <p key={c.id} data-testid={`market-source-${c.id}`}>
                <span className="text-[#1A1A1A]">{c.market}</span> ·{" "}
                {c.source}
              </p>
            ))}
            <p className="md:col-span-2 pt-2 border-t border-[#1A1A1A]/10 mt-2">
              <span className="text-[#1A1A1A]">Aggregate</span> ·{" "}
              {marketAggregate.source}
            </p>
            <p className="md:col-span-2 italic text-[#5C564D]/80">
              Figures are drawn from public data feeds and published operator
              rate cards at time of retrieval; illustrative of the market
              effect of luxury branding on comparable short-stay properties
              and not proprietary Maison Locale results except where labelled
              &ldquo;client&rdquo;.
            </p>
          </div>
        </details>

        {/* 3. Our flagship case study — now positioned AFTER market context */}
        <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-6 reveal">
            <p className="overline mb-6">Our Case Study</p>
            <h3 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] text-[#1A1A1A] font-light">
              What that
              <br />
              <span className="italic text-[#6B5C4D]">looks like</span> in
              practice.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 reveal flex md:justify-end">
            <p className="text-[#5C564D] text-lg leading-relaxed max-w-md">
              Mirrored Palms is a hillside estate in Paradise Valley,
              Arizona, and our first published short-stay engagement. It
              launched on <strong className="text-[#1A1A1A] font-medium">May 18, 2026</strong> with a full identity, guest
              collateral kit and photography direction — a complete brand
              system built to be lived in.
            </p>
          </div>
        </div>

        {/* Flagship hero card */}
        <div
          data-testid={`work-card-${heroProject.id}`}
          className="reveal grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 mb-8 md:mb-12"
        >
          <figure className="lg:col-span-8 relative overflow-hidden aspect-[4/3] lg:aspect-[3/2] group bg-[#EFEBE2]">
            <img
              src={heroProject.img}
              alt={heroProject.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
            <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-10 text-[#FAF9F6]">
              <p className="overline text-[#FAF9F6]/75 mb-2">Case · 01</p>
              <h3 className="font-serif-editorial text-3xl md:text-5xl leading-tight">
                {heroProject.title}
              </h3>
              <p className="text-sm md:text-base opacity-85 mt-2">
                {heroProject.location}
              </p>
            </figcaption>
          </figure>

          <div className="lg:col-span-4 border border-[#1A1A1A]/10 bg-white p-8 md:p-10 flex flex-col">
            <p className="overline mb-4">The Signal</p>
            <blockquote
              data-testid="mirrored-palms-pullquote"
              className="border-l-2 border-[#A89680] pl-5 mb-8"
            >
              <p className="font-serif-editorial text-2xl md:text-[1.65rem] italic text-[#1A1A1A] leading-snug">
                &ldquo;Our guest has already extended their stay for the
                third time.&rdquo;
              </p>
              <cite className="not-italic block mt-3 text-xs uppercase tracking-[0.22em] text-[#A89680]">
                — Owner, Mirrored Palms
              </cite>
            </blockquote>

            <ul className="flex-1 space-y-5">
              {heroProject.signals.map((k, i) => (
                <li
                  key={i}
                  data-testid={`work-kpi-${i}`}
                  className="border-t border-[#1A1A1A]/10 pt-4"
                >
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <p className="font-serif-editorial italic text-2xl md:text-[1.65rem] text-[#1A1A1A] leading-none">
                      {k.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#A89680]">
                      {k.label}
                    </p>
                  </div>
                  <p className="mt-2 text-sm text-[#5C564D] leading-relaxed">
                    {k.note}
                  </p>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#A89680]">
              Scope
            </p>
            <p className="mt-2 text-[#3E3227] leading-relaxed">
              {heroProject.scope}
            </p>

            <a
              href={heroProject.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mirrored-palms-listing-link"
              className="mt-8 inline-flex items-center h-12 px-5 border border-[#1A1A1A]/20 text-[11px] tracking-[0.24em] uppercase font-medium text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors duration-500 w-fit"
            >
              View the listing →
            </a>
          </div>
        </div>

        {/* Secondary images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16 md:mb-24">
          {secondaryImgs.map((s, i) => (
            <figure
              key={s.id}
              data-testid={`work-detail-${i}`}
              className="reveal group relative overflow-hidden aspect-[4/5] bg-[#EFEBE2]"
            >
              <img
                src={s.img}
                alt={s.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                <p className="overline text-[#FAF9F6]/85">
                  Detail · {String(i + 1).padStart(2, "0")}
                </p>
                <p className="text-[#FAF9F6] font-serif-editorial italic text-xl mt-1">
                  {s.caption}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Brand system — actual deliverables for Mirrored Palms */}
        <div
          data-testid="mirrored-palms-brand-system"
          className="reveal mb-24 md:mb-32"
        >
          <div className="grid grid-cols-12 gap-8 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-6">
              <p className="overline mb-6">The Brand System</p>
              <h3 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] text-[#1A1A1A] font-light">
                Palm, mark,
                <br />
                <span className="italic text-[#6B5C4D]">palette.</span>
              </h3>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex md:justify-end">
              <p className="text-[#5C564D] text-lg leading-relaxed max-w-md">
                A quiet, editorial identity built around a single motif —
                the palm — and a warm desert palette. Delivered as a full
                system across signage, stationery, guest collateral and
                photography watermarks.
              </p>
            </div>
          </div>

          {/* Row 1: flatlay of all brand items (housebook + brand board + notepad + card) */}
          <figure
            data-testid="brand-asset-board"
            className="relative overflow-hidden mb-6 md:mb-8 bg-[#EFEBE2]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/an9jv1sn_217f29c3-ed99-4740-8c83-c7b561387333.png"
              alt="Mirrored Palms — full brand collateral in-situ: housebook, brand board, thank-you card and notepad on marble counter"
              loading="lazy"
              className="w-full h-auto block"
            />
            <figcaption className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/85 backdrop-blur-md px-4 py-2 border border-black/5">
              <p className="overline">Brand Board · Full Collateral</p>
            </figcaption>
          </figure>

          {/* Row 2: in-situ deliverables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <figure
              data-testid="brand-asset-housebook"
              className="relative overflow-hidden aspect-[4/5] bg-[#EFEBE2] group"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/t6lt3dsf_ChatGPT%20Image%20Jul%203%2C%202026%2C%2002_44_10%20AM.png"
                alt="Mirrored Palms — welcome housebook on the kitchen counter"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                <p className="overline text-[#FAF9F6]/85">Deliverable · 01</p>
                <p className="text-[#FAF9F6] font-serif-editorial italic text-xl mt-1">
                  Welcome housebook
                </p>
              </figcaption>
            </figure>

            <figure
              data-testid="brand-asset-card"
              className="relative overflow-hidden aspect-[4/5] bg-[#EFEBE2] group"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/14oxgirv_ChatGPT%20Image%20Jul%203%2C%202026%2C%2002_30_22%20AM.png"
                alt="Mirrored Palms — thank-you card with property photo, in-situ on glass table"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                <p className="overline text-[#FAF9F6]/85">Deliverable · 02</p>
                <p className="text-[#FAF9F6] font-serif-editorial italic text-xl mt-1">
                  Thank-you card, in-situ
                </p>
              </figcaption>
            </figure>
          </div>
        </div>

        {/* 4. Revenue mechanics — the "why branding earns more" section (kept) */}
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6 reveal">
            <p className="overline mb-6">How Branding Earns More</p>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em] text-[#1A1A1A] font-light">
              Four levers.
              <br />
              <span className="italic text-[#6B5C4D]">Compounding,</span>
              <br />
              quarter over quarter.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 reveal flex md:justify-end">
            <p className="text-[#5C564D] text-lg leading-relaxed max-w-md">
              A brand is not a logo. It&apos;s the four mechanisms below,
              working together. Miss any one of them and the maths breaks.
              Get all four in place and revenue lifts &mdash; and keeps
              lifting.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {revenueMechanics.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={i}
                data-testid={`revenue-mechanic-${i}`}
                className="reveal border-t border-[#1A1A1A]/15 pt-8"
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-serif-editorial text-4xl text-[#A89680] font-light leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon
                    className="w-6 h-6 text-[#6B5C4D]"
                    strokeWidth={1.2}
                  />
                </div>
                <h3 className="mt-6 font-serif-editorial text-3xl italic text-[#1A1A1A]">
                  {m.title}
                </h3>
                <p className="mt-4 text-[#5C564D] text-lg leading-relaxed max-w-md">
                  {m.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
